/**
 * Name demangler for C++ (Itanium ABI _Z prefix) and Rust (v0 _R prefix, legacy _ZN).
 * Also decodes Rust symbol escapes ($LT$, $GT$, $u20$, etc.).
 * Falls back to the original name if demangling fails.
 */

export function demangleName(mangledName: string): string {
  // Rust v0 mangling: _R<...>
  if (mangledName.startsWith('_R')) {
    try {
      return demangleRustV0(mangledName);
    } catch {
      return mangledName;
    }
  }

  // Itanium ABI (C++ and Rust legacy): _Z<...>
  if (mangledName.startsWith('_Z')) {
    try {
      const result = demangleItanium(mangledName, 2);
      return stripRustHash(decodeRustEscapes(result));
    } catch {
      return mangledName;
    }
  }

  // Rust symbol escapes without _Z prefix (e.g. in some toolchains)
  if (mangledName.includes('$LT$') || mangledName.includes('$GT$') || mangledName.includes('$u20$')) {
    return decodeRustEscapes(mangledName);
  }

  return mangledName;
}

// Best-effort Itanium ABI demangler. Handles nested names, constructors,
// destructors, source names, and common operator encodings. Does not handle
// template arguments, const/volatile qualifiers, references, or variadic parameters.
function demangleItanium(input: string, position: number): string {
  // _Z<encoding>
  // <encoding> := <name> | <name> <bare-function-type>
  const result = parseName(input, position);
  return result.name;
}

interface ParseResult {
  name: string;
  position: number;
}

function parseName(input: string, position: number): ParseResult {
  if (position >= input.length) {
    return { name: input, position };
  }

  // Nested name: N<qualifier+><name>E
  if (input[position] === 'N') {
    position++;
    const parts: string[] = [];
    while (position < input.length && input[position] !== 'E') {
      const segment = parseUnqualifiedName(input, position);
      parts.push(segment.name);
      position = segment.position;
    }
    if (position < input.length) { position++; } // skip E
    return { name: parts.join('::'), position };
  }

  // Simple name
  return parseUnqualifiedName(input, position);
}

// Two-character Itanium operator encodings (e.g. "pl" = operator+, "eq" = operator==).
const ITANIUM_OPERATOR_NAMES: Record<string, string> = {
  'nw': 'operator new', 'na': 'operator new[]', 'dl': 'operator delete', 'da': 'operator delete[]',
  'ps': 'operator+', 'ng': 'operator-', 'ad': 'operator&', 'de': 'operator*',
  'co': 'operator~', 'pl': 'operator+', 'mi': 'operator-', 'ml': 'operator*',
  'dv': 'operator/', 'rm': 'operator%', 'an': 'operator&', 'or': 'operator|',
  'eo': 'operator^', 'aS': 'operator=', 'pL': 'operator+=', 'mI': 'operator-=',
  'mL': 'operator*=', 'dV': 'operator/=', 'eq': 'operator==', 'ne': 'operator!=',
  'lt': 'operator<', 'gt': 'operator>', 'le': 'operator<=', 'ge': 'operator>=',
  'ix': 'operator[]', 'cl': 'operator()', 'ls': 'operator<<', 'rs': 'operator>>',
};

/**
 * Parses an Itanium ABI unqualified name component.
 * Handles: C1/C2/C3 constructors, D0/D1/D2 destructors, decimal-length source names,
 * and two-character operator encodings (e.g., "pl" = operator+, "eq" = operator==).
 */
function parseUnqualifiedName(input: string, position: number): ParseResult {
  if (position >= input.length) {
    return { name: '', position };
  }

  if (input[position] === 'C') {
    position++;
    if (position < input.length) { position++; } // skip C1, C2, C3
    return { name: '{ctor}', position };
  }
  if (input[position] === 'D') {
    position++;
    if (position < input.length) { position++; }
    return { name: '{dtor}', position };
  }

  // Source name: <length> <identifier>
  if (input[position] >= '0' && input[position] <= '9') {
    let length = 0;
    while (position < input.length && input[position] >= '0' && input[position] <= '9') {
      length = length * 10 + (input.charCodeAt(position) - 48);
      position++;
    }
    const name = input.slice(position, position + length);
    return { name, position: position + length };
  }

  if (position + 1 < input.length) {
    const opName = ITANIUM_OPERATOR_NAMES[input.slice(position, position + 2)];
    if (opName) {
      return { name: opName, position: position + 2 };
    }
  }

  // Unrecognized component (substitution `S`, template `I…E`, CV-qualifier, …).
  // Bail so the caller falls back to the original symbol instead of emitting a
  // partially-consumed, corrupted name.
  throw new Error(`unsupported mangled component at position ${position}`);
}

// ─── Rust v0 demangler ───

/**
 * Demangles Rust v0 symbol encoding (_R prefix).
 * The encoding uses single-character tags for path components:
 *   N = namespace (followed by a tag byte: C=closure, v=value, etc.)
 *   C = crate root (followed by a length-prefixed identifier)
 *   M/X/Y = impl paths (skipped in simplified output)
 *   B = backreference to an earlier path (skipped in simplified output)
 *   I = generic arguments, s = disambiguator (skipped)
 *   E = end marker, h = hash suffix (terminates parsing)
 * Identifiers are decimal-length-prefixed with optional _ separator.
 */
function demangleRustV0(input: string): string {
  let position = 2; // skip _R
  const parts: string[] = [];

  while (position < input.length) {
    const nextPosition = stepRustV0(input, position, parts);
    if (nextPosition < 0) {
      break;
    }
    position = nextPosition;
  }

  if (parts.length === 0) {
    return input;
  }
  return parts.join('::');
}

// Consumes one Rust v0 path component starting at `position`, pushing any
// identifier onto `parts`. Returns the next position, or -1 to stop parsing
// (the hash suffix `h`).
function stepRustV0(input: string, position: number, parts: string[]): number {
  const char = input[position];
  if (char === 'N') {
    // Namespace: skip the tag byte (C=closure, v=value, etc.) that follows.
    return position + (position + 1 < input.length ? 2 : 1);
  }
  if (char === 'C') {
    const ident = parseRustV0Identifier(input, position + 1);
    parts.push(ident.name);
    return ident.position;
  }
  if (char === 'M' || char === 'X' || char === 'Y') {
    return position + 1;
  }
  if (char === 'B') {
    // Backreference: B <base-62 index> _. The referenced path prefix is already
    // represented by an earlier component here, so skip the whole backreference.
    let next = position + 1;
    while (next < input.length && input[next] !== '_') { next++; }
    return next + 1;
  }
  if (char >= '0' && char <= '9') {
    const ident = parseRustV0Identifier(input, position);
    parts.push(ident.name);
    return ident.position;
  }
  if (char === 'I' || char === 's' || char === 'E') {
    return position + 1;
  }
  if (char === 'h') {
    return -1;
  }
  return position + 1;
}

function parseRustV0Identifier(input: string, position: number): ParseResult {
  // Optional disambiguator: s<base-62-number>_
  if (position < input.length && input[position] === 's') {
    position++;
    while (position < input.length && input[position] !== '_') { position++; }
    if (position < input.length) { position++; } // skip _
  }

  // Length prefix (decimal)
  let length = 0;
  while (position < input.length && input[position] >= '0' && input[position] <= '9') {
    length = length * 10 + (input.charCodeAt(position) - 48);
    position++;
  }

  // Optional _ separator before identifier
  if (position < input.length && input[position] === '_') {
    position++;
  }

  const name = input.slice(position, position + length);
  return { name: decodeRustEscapes(name), position: position + length };
}

// ─── Rust escape decoder ───

const RUST_ESCAPES: Record<string, string> = {
  '$LT$': '<', '$GT$': '>', '$u20$': ' ', '$u27$': "'", '$u5b$': '[', '$u5d$': ']',
  '$u7b$': '{', '$u7d$': '}', '$u7e$': '~', '$u3b$': ';', '$u2b$': '+',
  '$u22$': '"', '$RF$': '&', '$BP$': '*', '$C$': ',', '$SP$': '@',
};

function stripRustHash(input: string): string {
  // Rust appends ::h<hex> as a hash suffix — strip it
  return input.replace(/::h[0-9a-fA-F]+E?$/, '');
}

function decodeRustEscapes(input: string): string {
  let result = input;
  for (const [escaped, decoded] of Object.entries(RUST_ESCAPES)) {
    result = result.split(escaped).join(decoded);
  }
  return result;
}
